import React from "react";
import styled from "styled-components";
import { color } from "styles/colours";
import { Text, types, Repeater } from "react-bricks/frontend";
import { H2 } from "components/atoms/Heading";
import { mobile } from "components/templates/Breakpoints";
import { VerticalPadding } from "../utils/verticalPadding";
import TextBlock from "../common/TextBlock";

export interface TextWithImageItemProps {
  eyebrow: boolean;
  hideBody: boolean;
  eyebrowColor: types.IColor;
  paddingVertical?: VerticalPadding;
  backgroundColor: types.IColor;
  showFooter?: boolean;
  textAlign?: "left" | "center" | "right" | "inherit";
}

export const TextWithHeadingBlock: types.Brick<TextWithImageItemProps> = ({
  eyebrow = false,
  hideBody = false,
  eyebrowColor,
  showFooter = false,
  textAlign = "left",
}) => {
  return (
    <TextContainer>
      {eyebrow && (
        <Eyebrow color={eyebrowColor?.color}>
          <Text
            propName="eyebrowText"
            placeholder="Eyebrow"
            renderBlock={({ children }) => <>{children}</>}
          />
        </Eyebrow>
      )}
      <TextTitle>
        <Text
          propName="title"
          placeholder="TextWithImage Title"
          renderBlock={({ children }) => <>{children}</>}
        />
      </TextTitle>

      {!hideBody && <TextBlock propName="text" align={textAlign} />}

      <Repeater
        propName="checklistItems"
        renderWrapper={(items) => <MarginTop>{items}</MarginTop>}
      />

      {showFooter ? (
        <MarginTop>
          <TextBlock propName="footer" />
        </MarginTop>
      ) : null}
    </TextContainer>
  );
};

TextWithHeadingBlock.schema = {
  name: "text-with-heading-block",
  label: "Text With Heading Block",
  hideFromAddMenu: true,
  getDefaultProps: () => ({
    title: "Small steps lead to a big change",
    text: "Lean on the principles of behavioural science to encourage simple everyday wellness activities, including 5-minute brisk walks and breathing exercises. These micro-interventions are the key to a healthy business.",
    footer: "Get your free demo—no pressure, just value.",
    showFooter: false,
    eyebrow: true,
    eyebrowColor: { color: color.S3100 },
    eyebrowText: "Featured",
  }),
  repeaterItems: [
    {
      name: "checklistItems",
      itemType: "checklist-item",
      itemLabel: "Checklist Item",
      min: 0,
      max: 8,
    },
  ],
  sideEditProps: [
    {
      name: "eyebrow",
      label: "Show Eyebrow",
      type: types.SideEditPropType.Boolean,
      shouldRefreshStyles: true,
    },
    {
      name: "hideBody",
      label: "Hide body text?",
      type: types.SideEditPropType.Boolean,
      shouldRefreshStyles: true,
    },
    {
      name: "showFooter",
      label: "Show footer text?",
      type: types.SideEditPropType.Boolean,
      shouldRefreshStyles: true,
    },
    {
      name: "eyebrowColor",
      label: "Eyebrow Color",
      type: types.SideEditPropType.Select,
      shouldRefreshStyles: true,
      show: (props) => props.eyebrow,
      selectOptions: {
        display: types.OptionsDisplay.Color,
        options: [
          { label: "purple", value: { color: color.S3100 } },
          { label: "blue", value: { color: color.S2100 } },
          { label: "pink", value: { color: color.P100 } },
          { label: "green", value: { color: "#28A87F" } },
        ],
      },
    },
    {
      name: "textAlign",
      label: "Text align",
      type: types.SideEditPropType.Select,
      shouldRefreshStyles: true,
      selectOptions: {
        display: types.OptionsDisplay.Select,
        options: [
          { label: "Left", value: "left" },
          { label: "Center", value: "center" },
          { label: "Right", value: "right" },
        ],
      },
    },
  ],
};

const MarginTop = styled.div`
  margin-top: 1rem;
`;

const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;
`;

const TextTitle = styled(H2)`
  font-size: 2.5rem;
  line-height: 2.625rem;
  margin-top: 0.5rem;
  margin-bottom: 1.5rem;

  ${mobile} {
    font-size: 2rem;
    line-height: 2.5rem;
  }
`;

const Eyebrow = styled.div<{
  color: string;
}>`
  font-weight: bold;
  font-size: 0.875rem;
  line-height: 1rem;
  color: ${(p) => p.color};
`;
